import { useMemo, useState, type ReactNode } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useLanguage } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ id, label, error, children }: { id: string; label: string; error?: string; children: ReactNode }) => (
  <div>
    <label htmlFor={id} className="mb-1.5 block text-sm font-medium">{label}</label>
    {children}
    {error && <p id={`${id}-error`} role="alert" className="mt-1.5 text-sm text-red-600" data-testid={`${id}-error`}>{error}</p>}
  </div>
);

export const ContactForm = () => {
  const { t, lang } = useLanguage();
  const v = t.form.validation;
  const [done, setDone] = useState(false);

  const schema = useMemo(() => z.object({
    name: z.string().min(1, v.nameRequired).min(2, v.nameMin),
    email: z.string().email(v.emailInvalid),
    phone: z.string().max(40).optional().or(z.literal("")),
    arrival: z.string().optional().or(z.literal("")),
    departure: z.string().optional().or(z.literal("")),
    persons: z.string().optional().or(z.literal("")).refine((s) => !s || (/^\d+$/.test(s) && +s >= 1 && +s <= 27), v.personsRange),
    message: z.string().min(10, v.messageMin),
    consent: z.literal(true, { errorMap: () => ({ message: v.consentRequired }) }),
  }).refine((d) => !d.arrival || !d.departure || d.departure > d.arrival, { message: v.departureAfterArrival, path: ["departure"] }), [v]);

  type Values = z.infer<typeof schema>;
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { consent: undefined as unknown as true } });

  const onSubmit = async (d: Values) => {
    try {
      await axios.post(`${API}/inquiries`, {
        name: d.name, email: d.email, phone: d.phone || null, arrival: d.arrival || null, departure: d.departure || null,
        persons: d.persons ? Number(d.persons) : null, message: d.message, consent: true, language: lang,
      });
      setDone(true);
      toast.success(t.form.successTitle);
    } catch (e) {
      const status = axios.isAxiosError(e) ? e.response?.status : undefined;
      toast.error(status === 429 ? t.form.rateLimited : t.form.error);
    }
  };

  if (done) {
    return (
      <div className="card p-8 md:p-10 text-center" role="status" data-testid="contact-form-success">
        <CheckCircle2 className="mx-auto h-12 w-12 text-clay" aria-hidden />
        <h3 className="h3 mt-4">{t.form.successTitle}</h3>
        <p className="mt-3 text-charcoal-500 leading-relaxed" data-testid="contact-form-success-message">{t.form.success}</p>
        <button type="button" className="btn-outline mt-6" onClick={() => { reset(); setDone(false); }} data-testid="contact-form-new">{t.form.newInquiry}</button>
      </div>
    );
  }

  const err = (k: keyof Values) => errors[k]?.message as string | undefined;

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="card p-6 md:p-8 grid gap-5" data-testid="contact-form">
      <div className="grid gap-5 sm:grid-cols-2">
        <Field id="name" label={t.form.name} error={err("name")}>
          <input id="name" className="field" autoComplete="name" placeholder={t.form.placeholders.name} aria-invalid={!!errors.name} aria-describedby={errors.name ? "name-error" : undefined} {...register("name")} data-testid="contact-name-input" />
        </Field>
        <Field id="email" label={t.form.email} error={err("email")}>
          <input id="email" type="email" className="field" autoComplete="email" placeholder={t.form.placeholders.email} aria-invalid={!!errors.email} aria-describedby={errors.email ? "email-error" : undefined} {...register("email")} data-testid="contact-email-input" />
        </Field>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field id="phone" label={t.form.phone} error={err("phone")}>
          <input id="phone" type="tel" className="field" autoComplete="tel" placeholder={t.form.placeholders.phone} {...register("phone")} data-testid="contact-phone-input" />
        </Field>
        <Field id="persons" label={t.form.persons} error={err("persons")}>
          <input id="persons" type="number" min={1} max={27} inputMode="numeric" className="field" placeholder={t.form.placeholders.persons} aria-invalid={!!errors.persons} {...register("persons")} data-testid="contact-persons-input" />
        </Field>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field id="arrival" label={t.form.arrival} error={err("arrival")}>
          <input id="arrival" type="date" className="field" {...register("arrival")} data-testid="contact-arrival-input" />
        </Field>
        <Field id="departure" label={t.form.departure} error={err("departure")}>
          <input id="departure" type="date" className="field" aria-invalid={!!errors.departure} {...register("departure")} data-testid="contact-departure-input" />
        </Field>
      </div>
      <Field id="message" label={t.form.message} error={err("message")}>
        <textarea id="message" rows={5} className="field resize-y" placeholder={t.form.placeholders.message} aria-invalid={!!errors.message} aria-describedby={errors.message ? "message-error" : undefined} {...register("message")} data-testid="contact-message-input" />
      </Field>
      <div>
        <label className="flex items-start gap-3 text-sm text-charcoal-700 cursor-pointer">
          <input type="checkbox" className="mt-1 h-4 w-4 shrink-0 accent-clay" aria-invalid={!!errors.consent} {...register("consent")} data-testid="contact-consent-checkbox" />
          <span>{t.form.consent} <Link to="/datenschutz" className="underline underline-offset-2 hover:text-clay">({t.form.consentLink})</Link></span>
        </label>
        {errors.consent && <p role="alert" className="mt-1.5 text-sm text-red-600" data-testid="consent-error">{errors.consent.message as string}</p>}
      </div>
      <button type="submit" disabled={isSubmitting} className="btn-primary justify-self-start !px-8 !py-3.5" data-testid="contact-submit-button">
        {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Send className="h-4 w-4" aria-hidden />}
        {isSubmitting ? t.form.sending : t.form.submit}
      </button>
    </form>
  );
};
